/* eslint-disable eqeqeq */
import { yupResolver } from '@hookform/resolvers/yup'
import { DatePicker } from 'antd'
import locale from 'antd/es/date-picker/locale/pt_BR'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { Button } from '../../../../components/Button'
import { FormGroup } from '../../../../components/FormElements/FormGroup'
import { Input } from '../../../../components/FormElements/Input'
import { InfiniteSelect } from '../../../../components/InfiniteSelect'
import { SelectWithSearch } from '../../../../components/SelectWithSearch'

import { handlerType } from '../../../../enumns/handlerType'
import { api } from '../../../../services/api'
import { useRequestCreateMutation } from '../../../../services/hooks/request/mutations/useRequestCreateMutation'
import { requestSchema } from '../../../../validators/requestSchema'

import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../constants/toast'
import { useRequestEditMutation } from '../../../../services/hooks/request/mutations/useRequestEditMutation'
import { type AssetAnalystDomain } from '../../../../types/domain/asset-analyst-domain'
import { type AssetDomain } from '../../../../types/domain/asset-domain'
import { type ClientDomain } from '../../../../types/domain/client-domain'
import { type RequestDomain } from '../../../../types/domain/request-domain'
import { type ResponsibleDomain } from '../../../../types/domain/responsible-domain'
import { type RequestPersistence } from '../../../../types/persistence/request-persistence'
import { getErrorMessage } from '../../../../utils/getErrorMessage'
import { invalidateQueriesList } from '../../../../utils/invalidateQueriesList'

type Props = {
  type: handlerType
  onClose: () => void
  id?: number
  defaultValue?: RequestDomain
}

export const RequestForm = ({ type, onClose, id, defaultValue }: Props) => {
  const { mutate: createMutation } = useRequestCreateMutation()
  const { mutate: editMutation } = useRequestEditMutation()
  const queryClient = useQueryClient()

  const [searchResponsible, setSearchResponsible] = useState('')
  const [searchClient, setSearchClient] = useState('')
  const [searchAsset, setSearchAsset] = useState('')
  const [openDatePicker, setOpenDatePicker] = useState(false)
  const [putAnalystDefaultValue, setPutAnalystDefaultValue] = useState(false)
  const [putRequesterDefaultValue, setPutRequesterDefaultValue] =
    useState(false)

  const { control, formState, handleSubmit, watch, resetField } =
    useForm<RequestPersistence>({
      mode: 'onTouched',
      resolver: yupResolver(requestSchema),
      defaultValues: {
        responsibleId: defaultValue?.responsible?.id,
        clientId: defaultValue?.client?.id,
        requesterId: defaultValue?.requester?.id,
        projectName: defaultValue?.projectName,
        assetId: defaultValue?.asset?.id,
        analystId: defaultValue?.analyst?.id,
        enterAt: defaultValue?.enterAt
          ? new Date(defaultValue.enterAt)
          : undefined,
        description: defaultValue?.description,
        responsibleType: defaultValue?.responsible?.user.type,
      },
    })

  const currentClientIdValue = watch('clientId')
  const currentAssetIdValue = watch('assetId')

  // Responsible Select Info -------------------------------------------------------------
  const {
    data: responsibleData,
    hasNextPage: responsibleHasNextPage,
    fetchNextPage: responsibleFetchNextPage,
  } = useInfiniteQuery(
    ['responsible-select', searchResponsible],
    () =>
      api
        .get(`/responsibles?search=${searchResponsible}`)
        .then((response) => response.data),
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const responsibleOptions = useMemo(() => {
    const allResponsibles: ResponsibleDomain[] =
      responsibleData?.pages.reduce((acc, data) => {
        return [...acc, ...data]
      }, []) || []

    const options = allResponsibles.map((responsible: ResponsibleDomain) => ({
      label: responsible.user?.name,
      value: String(responsible.id),
    }))

    const defaultResponsible = defaultValue?.responsible
    const responsibleAlreadySelected = allResponsibles.some(
      (responsible) => String(responsible.id) === String(defaultResponsible?.id)
    )

    if (defaultResponsible && !responsibleAlreadySelected) {
      options.push({
        label: defaultResponsible.user?.name,
        value: String(defaultResponsible.id),
      })
    }

    return options
  }, [responsibleData, defaultValue?.responsible])

  // Client Select Info -------------------------------------------------------------
  const {
    data: clienteData,
    hasNextPage: clientHasNextPage,
    fetchNextPage: clientFetchNextPage,
  } = useInfiniteQuery(
    ['client-select', searchClient],
    ({ pageParam = 1 }) =>
      api
        .get(
          `/clients?page=${pageParam}&search=${searchClient}&sort=name&order=ASC`
        )
        .then((response) => response.data),
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const allClients: ClientDomain[] = useMemo(() => {
    return (
      clienteData?.pages.reduce((acc, { data }) => [...acc, ...data], []) || []
    )
  }, [clienteData])

  const clientOptions = useMemo(() => {
    const options = allClients.map((client) => ({
      label: client.name,
      value: String(client.id),
    }))

    const defaultClient = defaultValue?.client
    const clientAlreadySelected = allClients.some(
      (client) => String(client.id) === String(defaultClient?.id)
    )

    if (defaultClient && !clientAlreadySelected) {
      options.push({
        label: defaultClient.name,
        value: String(defaultClient.id),
      })
    }

    return options
  }, [allClients, defaultValue?.client])

  // Requester Select Info -------------------------------------------------------------
  const requesterOptions = useMemo(() => {
    let currentClientContacts = []

    if (type === handlerType.EDIT) {
      const defaultValueClientId = defaultValue?.client?.id
      const defaultValueClientContacts = defaultValue?.client?.contacts || []

      if (currentClientIdValue == defaultValueClientId) {
        currentClientContacts = defaultValueClientContacts
      } else {
        const client = allClients.find(
          ({ id }) => String(id) == String(currentClientIdValue)
        )
        currentClientContacts = client?.contacts || []
      }
    } else {
      const client = allClients.find(({ id }) => id == currentClientIdValue)
      currentClientContacts = client?.contacts || []
    }

    return currentClientContacts
      .sort((a, b) => Intl.Collator().compare(a.name, b.name))
      .map((contact) => ({
        label: contact.name,
        value: String(contact.id),
      }))
  }, [type, defaultValue?.client, allClients, currentClientIdValue])

  useEffect(() => {
    resetField('requesterId', {
      defaultValue: undefined,
      keepDirty: false,
      keepError: false,
      keepTouched: false,
    })
  }, [currentClientIdValue])

  // Asset Select Info -------------------------------------------------------------
  const {
    data: assetData,
    hasNextPage: assetHasNextPage,
    fetchNextPage: assetFetchNextPage,
  } = useInfiniteQuery(
    ['asset-select', searchAsset],
    ({ pageParam = 1 }) =>
      api
        .get(
          `/assets?page=${pageParam}&search=${searchAsset}&sort=fullName&order=ASC`
        )
        .then((response) => response.data),
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const allAssets: AssetDomain[] = useMemo(() => {
    return (
      assetData?.pages.reduce((acc, { data }) => [...acc, ...data], []) || []
    )
  }, [assetData])

  const assetOptions = useMemo(() => {
    const options = allAssets.map(({ id, fullName }) => ({
      label: fullName,
      value: String(id),
    }))

    const assetAlreadySelected = allAssets.some(
      ({ id }) => id === defaultValue?.asset?.id
    )

    if (defaultValue?.asset && !assetAlreadySelected) {
      options.push({
        label: defaultValue.asset.fullName,
        value: String(defaultValue.asset.id),
      })
    }

    return options
  }, [allAssets, defaultValue?.asset])

  const analystOptions = useMemo(() => {
    let currentAssetAnalysts = []

    if (type === handlerType.EDIT) {
      const defaultValueAssetId = defaultValue?.asset?.id
      const defaultValueAnalysts = defaultValue?.asset?.analysts || []

      if (currentAssetIdValue == defaultValueAssetId) {
        currentAssetAnalysts = defaultValueAnalysts
      } else {
        const asset = allAssets.find(({ id }) => id == currentAssetIdValue)
        currentAssetAnalysts = asset?.analysts || []
      }
    } else {
      const asset = allAssets.find(({ id }) => id == currentAssetIdValue)
      currentAssetAnalysts = asset?.analysts || []
    }

    return currentAssetAnalysts
      .sort((a, b) => Intl.Collator().compare(a.fullName, b.fullName))
      .map((analyst: AssetAnalystDomain) => ({
        label: analyst.fullName,
        value: String(analyst.id),
      }))
  }, [type, defaultValue?.asset?.analysts, allAssets, currentAssetIdValue])

  useEffect(() => {
    resetField('analystId', {
      defaultValue: undefined,
      keepDirty: false,
      keepError: false,
      keepTouched: false,
    })
  }, [currentAssetIdValue])

  // Format data ---------------------------------------------------------------------
  const formatData = (data: RequestPersistence) => {
    let responsible = responsibleData?.pages[0]?.find(
      (item: ResponsibleDomain) => item.id === Number(data.responsibleId)
    )

    data.responsibleId = Number(responsible?.id)
    data.responsibleType = responsible?.user.type

    data.clientId = Number(data.clientId)
    data.requesterId = Number(data.requesterId)
    data.assetId = Number(data.assetId)
    data.analystId = Number(data.analystId)
    data.enterAt = new Date(data.enterAt)

    return data
  }

  // Handler --------------------------------------------------------------------------
  const saveHandler = (data: RequestPersistence) => {
    data = formatData(data)

    createMutation(data, {
      onSuccess() {
        onClose()
        invalidateQueriesList(queryClient, [
          ['requests'],
          '@requests/responsibleNames',
          '@requests/clientNames',
          '@requests/requesterNames',
          '@requests/projectNames',
          '@requests/assetNames',
          '@requests/assetAnalystNames',
        ])
        toast('Request criado com sucesso', TOAST_SUCCESS_DEFAULT_OPTIONS)
      },
      onError(error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const editHandler = (data: any) => {
    if (id) {
      data = formatData(data)
      editMutation(
        { id, data },
        {
          onSuccess() {
            onClose()
            invalidateQueriesList(queryClient, [
              ['requests'],
              ['request', id],
              '@requests/responsibleNames',
              '@requests/clientNames',
              '@requests/requesterNames',
              '@requests/projectNames',
              '@requests/assetNames',
              '@requests/assetAnalystNames',
            ])
            toast('Request editado com sucesso', TOAST_SUCCESS_DEFAULT_OPTIONS)
          },
          onError(error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
    }
  }

  // useEffect ---------------------------------------------------------------------------
  useEffect(() => {
    if (defaultValue && requesterOptions && !putRequesterDefaultValue) {
      resetField('requesterId', {
        defaultValue: Number(defaultValue?.requester?.id),
        keepDirty: false,
        keepError: false,
        keepTouched: false,
      })
      setPutRequesterDefaultValue(true)
    }
  }, [requesterOptions])

  useEffect(() => {
    if (defaultValue && analystOptions && !putAnalystDefaultValue) {
      resetField('analystId', {
        defaultValue: Number(defaultValue?.analyst?.id),
        keepDirty: false,
        keepError: false,
        keepTouched: false,
      })
      setPutAnalystDefaultValue(true)
    }
  }, [analystOptions])

  return (
    <form
      className="px-4 py-5 max-h-[calc(100vh_-_120px)] overflow-y-auto scrollbar"
      onSubmit={handleSubmit(
        type === handlerType.SAVE ? saveHandler : editHandler
      )}
    >
      <div className="flex flex-col gap-4">
        <Controller
          name="responsibleId"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup
                label="Responsável pelo registro"
                error={error?.message}
              >
                <InfiniteSelect
                  placeholder="Selecione o responsável"
                  searchInputPlaceholder="Buscar pelo responsável"
                  hasNextPage={responsibleHasNextPage}
                  onLoadMoreOptions={responsibleFetchNextPage}
                  options={responsibleOptions}
                  defaultValue={String(value)}
                  onValueChange={(value) => onChange(value)}
                  onSearch={(searchTerm: string) =>
                    setSearchResponsible(searchTerm)
                  }
                />
              </FormGroup>
            )
          }}
        />

        <Controller
          name="clientId"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup label="Cliente" error={error?.message}>
                <InfiniteSelect
                  placeholder="Selecione o cliente"
                  searchInputPlaceholder="Buscar pelo cliente"
                  hasNextPage={clientHasNextPage}
                  onLoadMoreOptions={clientFetchNextPage}
                  options={clientOptions}
                  defaultValue={String(value)}
                  onValueChange={(value) => onChange(value)}
                  onSearch={(searchTerm: string) => setSearchClient(searchTerm)}
                />
              </FormGroup>
            )
          }}
        />

        <Controller
          name="requesterId"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup label="Solicitante" error={error?.message}>
                <SelectWithSearch
                  placeholder="Selecione o solicitante"
                  searchPlaceholder="Buscar pelo solicitante"
                  noRecordMessage="Sem contato cadastrado neste cliente "
                  value={value ? String(value) : undefined}
                  onValueChange={(value) => onChange(value)}
                  defaultValue={value ? String(value) : undefined}
                  options={requesterOptions}
                  disabled={!currentClientIdValue}
                />
              </FormGroup>
            )
          }}
        />

        <Controller
          name="projectName"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { invalid, error },
          }) => (
            <Input
              label="Projeto"
              errorMessage={error?.message}
              autoFocus
              placeholder="Projeto"
              isValid={!invalid && !!error}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="assetId"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup label="Asset" error={error?.message}>
                <InfiniteSelect
                  placeholder="Selecione o asset"
                  searchInputPlaceholder="Buscar pelo asset"
                  hasNextPage={assetHasNextPage}
                  onLoadMoreOptions={assetFetchNextPage}
                  options={assetOptions}
                  defaultValue={String(value)}
                  onValueChange={(value) => onChange(value)}
                  onSearch={(searchTerm: string) => setSearchAsset(searchTerm)}
                />
              </FormGroup>
            )
          }}
        />

        <Controller
          name="analystId"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup label="Analista" error={error?.message}>
                <SelectWithSearch
                  placeholder="Selecione o analista"
                  searchPlaceholder="Buscar pelo analista"
                  noRecordMessage="Sem analista cadastrado neste asset "
                  value={value ? String(value) : undefined}
                  onValueChange={onChange}
                  defaultValue={value ? String(value) : undefined}
                  options={analystOptions}
                  disabled={!currentAssetIdValue}
                />
              </FormGroup>
            )
          }}
        />

        <Controller
          name="enterAt"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { invalid, error },
          }) => (
            <FormGroup label="Data de entrada" error={error?.message}>
              <div>
                <DatePicker
                  className="h-[50px] w-full"
                  style={{
                    fontFamily: 'Inter',
                    color: '#404042',
                    fontSize: '14px',
                  }}
                  locale={locale}
                  format="DD/MM/YYYY"
                  placeholder="00/00/0000"
                  onChange={(value) => {
                    onChange(value)
                    setOpenDatePicker(false)
                  }}
                  onBlur={() => {
                    setOpenDatePicker(false)
                  }}
                  onMouseDown={(event) => {
                    let element = event.target as HTMLElement
                    element.tagName.toUpperCase() === 'INPUT' &&
                      setOpenDatePicker(true)
                  }}
                  open={openDatePicker}
                  value={
                    !value || value === 'Invalid Date'
                      ? undefined
                      : dayjs(dayjs(value).format('DD/MM/YYYY'), 'DD/MM/YYYY')
                  }
                  getPopupContainer={() => {
                    return document.getElementById('date-popup') as HTMLElement
                  }}
                />
                <div id="date-popup" />
              </div>
            </FormGroup>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => {
            return (
              <FormGroup label="Descrição" error={error?.message}>
                <textarea
                  className="w-full p-3  font-normal text-grey-800 text-xs lg:text-sm rounded-lg h-28 border-1 border-grey-500 focus:!outline-none focus:border-spacing-1 focus:border-solid focus:border-grey-700 placeholder:text-grey-500 placeholder:font-sans"
                  onChange={onChange}
                  value={value}
                  placeholder="Descrição"
                />
              </FormGroup>
            )
          }}
        />

        <div className="mt-10">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={formState.isSubmitting}
            disabled={!formState.isValid}
          >
            {type === handlerType.SAVE ? 'Cadastrar' : 'Salvar'}
          </Button>
        </div>
      </div>
    </form>
  )
}
